import styles from '@components/components/EnergieSparCheck/FinalStep.module.scss';
import Container from '@components/components/Container/Container';
import classNames from 'classnames';
import {
  BlogCard,
  Card,
  CardBody,
  Content,
  FontAwesomeProIcon,
  Heading,
  Link,
  PieChart,
} from '@wienenergiegithub/ui-next';
import React from 'react';

const PieChartComponent = ({ resultsWithPosts }) => (
  <Card className={styles.resultsChartContainer}>
    <CardBody>
      <Heading as="h3" className={styles.smallSpacing} size="h4">
        Verbrauchsverteilung
      </Heading>
      <Content noSpacing>
        In den Bereichen{' '}
        <span
          className={styles.resultName}
          style={{ color: resultsWithPosts[0].color }}
        >
          {resultsWithPosts[0]?.name}
        </span>{' '}
        sowie{' '}
        <span
          className={styles.resultName}
          style={{ color: resultsWithPosts[1].color }}
        >
          {resultsWithPosts[1]?.name}
        </span>{' '}
        können Sie am meisten Energie einsparen.
      </Content>
      <div className={styles.pieChart}>
        <PieChart
          className={styles.pieChart}
          colors={resultsWithPosts.map(result => result.color)}
          data={resultsWithPosts.map(result => result)}
        />
      </div>
      <div className={styles.legendContainer}>
        {resultsWithPosts.map(result => (
          <div className={styles.legendChild} key={result.name}>
            <div
              className={styles.colorDot}
              style={{ backgroundColor: result.color }}
            />
            <Heading as="div" className={styles.percentage} noSpacing size="h4">
              {result.value}%
            </Heading>
            <Content className={styles.legendContent} noSpacing>
              {result.name}
            </Content>
          </div>
        ))}
      </div>
    </CardBody>
  </Card>
);
const AlertCardDecider = ({ result }) => {
  if (result.name === 'Klimageräte') {
    return (
      <Card className={styles.alertCard}>
        <CardBody>
          <Heading as="div" noSpacing size="h2" theme="contrast">
            Nur 0,3% der Wiener*innen denken beim Thema Energiesparen zuerst an
            die Klimaanalage.
          </Heading>
        </CardBody>
      </Card>
    );
  }
  return (
    <Card className={styles.alertCard}>
      <CardBody>
        <Heading as="h3" size="h4" theme="contrast">
          Ihr Verbrauch in diesem Bereich:
        </Heading>
        <Heading as="div" noSpacing size="h2" theme="contrast">
          Ca. {Math.abs(result.average.percentage)} %{' '}
          {result.average.percentage > 0 ? 'über' : 'unter'} dem Durchschnitt
        </Heading>
      </CardBody>
    </Card>
  );
};

const TotalPercentageText = ({ results }) => {
  const total = results.reduce((acc, result) => acc + result.kwh, 0);
  const totalAverage = results.reduce(
    (acc, result) => acc + result.average.value,
    0,
  );
  const sub = total - totalAverage;

  const totalPercentage = Math.round((sub / totalAverage) * 100);
  if (totalPercentage > 0) {
    return (
      <Heading as="div" className={styles.red} noSpacing size="h1">
        ca. {Math.abs(totalPercentage)} % über dem Durchschnitt
      </Heading>
    );
  }
  return (
    <Heading as="div" className={styles.green} noSpacing size="h1">
      ca. {Math.abs(totalPercentage)} % unter dem Durchschnitt
    </Heading>
  );
};

const FinalStep = ({ mainPosts, results }) => {
  const resultsWithPosts = results
    ?.map(result => {
      const [posts] = mainPosts.filter(post => post.name === result.name);
      return { ...result, posts };
    })
    .sort((a, b) => b.value - a.value);

  return (
    <div className={styles.container}>
      <Container size="lg">
        <div className={classNames(styles.row, styles.titleRowMargin)}>
          <div className={classNames(styles.column, styles.noGap)}>
            <Heading
              as="h2"
              className={styles.smallSpacing}
              role="status"
              size="h2"
            >
              Ihr Ergebnis
            </Heading>
            <Content noSpacing>
              Jetzt sehen Sie, wo die größten Stromfresser in Ihrem Zuhause
              sind. Wir geben Ihnen Tipps, wie Sie dort Energie sparen, wo Sie
              derzeit am meisten verbrauchen.
            </Content>
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.column}>
            <Card className={styles.factCard}>
              <CardBody>
                <Heading as="h3" className={styles.smallSpacing} size="h4">
                  Ihr aktueller Gesamtverbrauch
                </Heading>
                <TotalPercentageText results={results} />

                <Content className={styles.legal} noSpacing>
                  Alle Berechnungen basieren auf durchschnittlichen
                  Verbrauchswerten.
                </Content>
              </CardBody>
            </Card>
          </div>
          <div className={styles.column}>
            <PieChartComponent resultsWithPosts={resultsWithPosts} />
          </div>
        </div>
        {resultsWithPosts.map((result, index) => (
          <div className={styles.marginTop} key={result.name}>
            <div className={classNames(styles.row, styles.titleRowMargin)}>
              <div className={classNames(styles.column, styles.noGap)}>
                <Heading as="h2" className={styles.smallSpacing} size="h2">
                  {result.name}
                </Heading>
                {index === 0 && (
                  <Content noSpacing>
                    In diesem Bereich ist Ihr Stromverbrauch am höchsten. Sie
                    finden passende Tipps, wie Sie hier Energie sparen können in
                    dem Artikel.
                  </Content>
                )}
                {index === 1 && (
                  <Content noSpacing>
                    In diesem Bereich ist Ihr Stromverbrauch am zweithöchsten.
                    Wir haben auch hier für Sie passende Energiespar-Tipps in
                    einem Artikel zusammengestellt.
                  </Content>
                )}
              </div>
            </div>

            <AlertCardDecider result={result} />
            <div className={styles.row}>
              <div className={styles.column}>
                <Card className={styles.factCard}>
                  <CardBody>
                    <Heading
                      as="h3"
                      className={styles.smallSpacing}
                      noSpacing
                      size="h4"
                    >
                      Ungefährer Stromverbrauch
                    </Heading>
                    <Heading
                      aria-label={`${result.kwh} Kilowattstunden`}
                      as="div"
                      noSpacing
                      size="h1"
                    >
                      {result.kwh.toLocaleString('de')} kWh
                    </Heading>
                    {result.name !== 'Klimageräte' && (
                      <Content noSpacing>
                        Durchschnitt:{' '}
                        {result.average.value.toLocaleString('de')} kWh
                      </Content>
                    )}
                  </CardBody>
                </Card>
                <Card className={styles.factCard}>
                  <CardBody>
                    <Heading as="h3" size="h4">
                      Mit einer kWh kann man circa
                    </Heading>
                    <div className={styles.tippRow}>
                      {result.tipps.map(tipp => (
                        <div className={styles.tipps} key={tipp.title}>
                          <FontAwesomeProIcon
                            className={styles.icon}
                            icon={tipp.icon}
                            size="2x"
                          />
                          <Heading as="div" noSpacing size="h4">
                            {tipp.title}
                          </Heading>
                          <Content>{tipp.content}.</Content>
                        </div>
                      ))}
                    </div>
                    <Content noSpacing>
                      Mehr Informationen rund um die Kilowattstunde finden Sie
                      <Link
                        tabIndex={-1}
                        target="_blank"
                        to="/blog/was-ist-eine-kilowattstunde/"
                      >
                        hier.
                      </Link>
                    </Content>
                  </CardBody>
                </Card>
              </div>
              <div className={styles.column}>
                {result.posts && (
                  <BlogCard
                    target="_blank"
                    {...result.posts}
                    className={styles.blogCard}
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </Container>
    </div>
  );
};

export default FinalStep;
